(() => {
  // Keep this asset revision coupled to the variant-gallery lifecycle release.
  const ROOT_SELECTOR = '#section-quick-product-v2[data-version-gallery-enabled="true"]';
  const instances = new Map();

  function normalize(value) {
    return String(value || '').trim().toLowerCase();
  }

  function colorSlug(value) {
    return normalize(value)
      .replace(/\s*&\s*/g, '-')
      .replace(/[\s/]+/g, '-')
      .replace(/-+/g, '-');
  }

  function removeGalleryColorClasses(element) {
    Array.from(element.classList).forEach((className) => {
      if (className.startsWith('alt-')) element.classList.remove(className);
    });
  }

  function selfOrDescendant(element, selector) {
    if (!element) return null;
    return element.matches(selector) ? element : element.querySelector(selector);
  }

  class VariantGallery {
    constructor(root) {
      this.root = root;
      this.form = root.querySelector('.ProductForm');
      this.generation = 0;
      this.syncFrame = null;
      this.surfaceFrames = new Map();
      this.surfaceTransitions = new Map();
      this.swipers = new Set();
      this.swiperElements = new Map();
      this.destroyed = false;
      this.handleChange = this.handleChange.bind(this);
      this.handleLoad = this.handleLoad.bind(this);

      if (!this.form || !root.querySelector('[data-ebike-version-gallery-group]')) return;

      this.form.addEventListener('change', this.handleChange);
      this.initialVariantId = this.selectedVariantId();
      this.alignInitialFamilyToVariant();
      this.scheduleSync();
      if (document.readyState === 'complete') {
        this.handleLoad();
      } else {
        window.addEventListener('load', this.handleLoad, { once: true });
      }
      root.dataset.ebikeVersionGalleryReady = 'true';
    }

    selectedInput(selector) {
      return Array.from(this.form.querySelectorAll(selector)).find((input) => input.checked);
    }

    selectedColor() {
      return this.selectedInput(
        'input[type="radio"][data-variant="Color"], input[type="radio"][data-variant="Colour"], input[name="option-0"]'
      )?.value || '';
    }

    selectedSize() {
      return this.selectedInput(
        'input[type="radio"][data-variant="Size"], input[type="radio"][data-variant="size"], input[name="option-2"]'
      )?.value || '';
    }

    selectedFamily() {
      const inputs = Array.from(this.form.querySelectorAll(
        '.variant-option.version input[type="radio"], input[type="radio"][data-variant="Helmet Type"], input[type="radio"][data-variant="Model"], input[type="radio"][data-variant="Select A Bundle"]'
      ));
      const input = inputs.find((candidate) => (
        candidate.checked && !candidate.disabled && candidate.closest('.extra-bundle')
      )) || inputs.find((candidate) => candidate.checked && !candidate.disabled);
      if (!input) return '';

      const value = normalize(input?.dataset.value || input?.value);
      return value.includes('standard') || value.includes('non-mips') ? 'standard' : 'mips';
    }

    selectedVariantId() {
      const select = this.form.querySelector('select[name="id"]');
      if (select?.value) return String(select.value);
      const input = this.form.querySelector('input[name="id"]');
      return input?.value ? String(input.value) : '';
    }

    alignInitialFamilyToVariant() {
      const variantId = this.selectedVariantId();
      if (!variantId) return;

      const variantGroup = Array.from(this.root.querySelectorAll('[data-ebike-version-gallery-group]')).find((group) => (
        String(group.dataset.ebikeGalleryVariantId || '') === variantId
      ));
      const family = normalize(variantGroup?.dataset.ebikeGalleryFamily);
      if (!family) return;

      const versionInputs = Array.from(this.form.querySelectorAll(
        '.variant-option.version input[type="radio"], input[type="radio"][data-variant="Helmet Type"], input[type="radio"][data-variant="Model"], input[type="radio"][data-variant="Select A Bundle"]'
      ));
      const target = versionInputs.find((input) => {
        const value = normalize(input.dataset.value || input.value);
        if (family === 'standard') return value.includes('standard') || value.includes('non-mips');
        return value === 'mips';
      });

      if (target && !target.checked) target.checked = true;
    }

    handleLoad() {
      if (this.destroyed || this.selectedVariantId() !== this.initialVariantId) return;
      this.alignInitialFamilyToVariant();
      this.scheduleSync();
    }

    handleChange(event) {
      if (event.target.matches('input[type="radio"], select[name="id"]')) {
        this.scheduleSync();
      }
    }

    isCurrentGeneration(generation) {
      return !this.destroyed && generation === this.generation;
    }

    cancelPendingFrames() {
      if (this.syncFrame !== null) {
        window.cancelAnimationFrame(this.syncFrame);
        this.syncFrame = null;
      }

      this.surfaceFrames.forEach((frame) => window.cancelAnimationFrame(frame));
      this.surfaceFrames.clear();
    }

    cancelSurfaceTransition(surface) {
      if (!this.surfaceTransitions) this.surfaceTransitions = new Map();
      const transition = this.surfaceTransitions.get(surface);
      if (!transition) return;

      this.surfaceTransitions.delete(surface);
      transition.cancel();
    }

    cancelPendingSurfaceTransitions() {
      if (!this.surfaceTransitions) return;
      Array.from(this.surfaceTransitions.keys()).forEach((surface) => {
        this.cancelSurfaceTransition(surface);
      });
    }

    scheduleSync() {
      this.generation += 1;
      const generation = this.generation;
      this.cancelPendingSurfaceTransitions();
      this.cancelPendingFrames();

      const settleFrame = window.requestAnimationFrame(() => {
        if (this.syncFrame !== settleFrame || !this.isCurrentGeneration(generation)) return;

        const syncFrame = window.requestAnimationFrame(() => {
          if (this.syncFrame !== syncFrame || !this.isCurrentGeneration(generation)) return;
          this.syncFrame = null;
          this.sync(generation);
        });
        this.syncFrame = syncFrame;
      });
      this.syncFrame = settleFrame;
    }

    scheduleSurfaceInitialization(surface, callback, generation) {
      const previousFrame = this.surfaceFrames.get(surface);
      if (previousFrame !== undefined) window.cancelAnimationFrame(previousFrame);

      const frame = window.requestAnimationFrame(() => {
        if (this.surfaceFrames.get(surface) !== frame || !this.isCurrentGeneration(generation)) return;
        this.surfaceFrames.delete(surface);
        callback();
      });
      this.surfaceFrames.set(surface, frame);
    }

    beginSurfaceTransition(surface) {
      this.cancelSurfaceTransition(surface);

      const transition = {
        active: true,
        cleanups: new Set(),
        addCleanup: (cleanup) => {
          if (!transition.active) {
            cleanup();
            return;
          }
          transition.cleanups.add(cleanup);
        },
        removeCleanup: (cleanup) => {
          transition.cleanups.delete(cleanup);
        },
        cancel: () => {
          if (!transition.active) return;
          transition.active = false;
          Array.from(transition.cleanups).forEach((cleanup) => cleanup());
          transition.cleanups.clear();
          if (this.surfaceTransitions.get(surface) === transition) {
            this.surfaceTransitions.delete(surface);
          }
        },
        finish: () => {
          transition.cancel();
        }
      };

      this.surfaceTransitions.set(surface, transition);
      return transition;
    }

    mainImageForScope(scope) {
      if (!scope) return null;

      const main = scope.matches?.('[data-ebike-version-gallery-main]')
        ? scope
        : scope.querySelector?.('[data-ebike-version-gallery-main]');
      return main?.querySelector?.('img') || null;
    }

    firstTargetImage(selectedGroups, legacyGalleries, color, useVersionGroup) {
      if (useVersionGroup) {
        return selectedGroups
          .map((group) => this.mainImageForScope(group))
          .find(Boolean) || null;
      }

      const selectedLegacyMain = legacyGalleries.find((gallery) => (
        gallery.matches?.('[data-ebike-legacy-main]')
        && normalize(gallery.dataset?.ebikeGalleryColor) === normalize(color)
      ));
      return this.mainImageForScope(selectedLegacyMain) || selectedLegacyMain?.querySelector?.('img') || null;
    }

    visibleSurfaceElements(groups, legacyGalleries) {
      return [...groups, ...legacyGalleries].filter((element) => element && !element.hidden);
    }

    restoreVisibleSurface(elements, color) {
      elements.forEach((element) => {
        if (!element) return;
        element.hidden = false;

        const showcases = element.matches?.('.product-showcase')
          ? [element, ...Array.from(element.querySelectorAll?.('.product-showcase') || [])]
          : Array.from(element.querySelectorAll?.('.product-showcase') || []);
        showcases.forEach((showcase) => {
          removeGalleryColorClasses(showcase);
          showcase.classList.add('active');
          const showcaseColor = showcase.dataset?.ebikeGalleryColor
            || element.dataset?.ebikeGalleryColor
            || color;
          const className = 'alt-' + colorSlug(showcaseColor);
          if (className !== 'alt-') showcase.classList.add(className);
        });
      });
    }

    preloadImage(image) {
      if (!image) return;
      image.loading = 'eager';
      image.setAttribute?.('loading', 'eager');
      // Keep the server-selected responsive source. Setting loading eagerly is
      // enough to make a hidden lazy image eligible for the browser preload.
    }

    waitForImage(image, generation, transition) {
      if (!image) return Promise.resolve(true);
      this.preloadImage(image);

      const hasDrawablePixels = (assumeLoaded = false) => {
        if (Number.isFinite(image.naturalWidth)) return image.naturalWidth > 0;
        return assumeLoaded || image.complete === true;
      };

      if (!image.addEventListener || !image.removeEventListener) {
        return Promise.resolve(hasDrawablePixels());
      }

      return new Promise((resolve) => {
        let settled = false;

        const cleanup = () => {
          image.removeEventListener('load', onLoad);
          image.removeEventListener('error', onError);
          transition.removeCleanup(cleanup);
        };
        const settle = (ready) => {
          if (settled) return;
          settled = true;
          cleanup();
          resolve(Boolean(ready && this.isCurrentGeneration(generation) && transition.active));
        };
        const decodeAfterLoad = (assumeLoaded) => {
          if (!this.isCurrentGeneration(generation) || !transition.active) {
            settle(false);
            return;
          }

          if (typeof image.decode !== 'function') {
            settle(hasDrawablePixels(assumeLoaded));
            return;
          }

          let decoded;
          try {
            decoded = image.decode();
          } catch {
            settle(hasDrawablePixels(assumeLoaded));
            return;
          }
          Promise.resolve(decoded).then(
            () => settle(hasDrawablePixels(assumeLoaded)),
            () => settle(false),
          );
        };
        const onLoad = () => decodeAfterLoad(true);
        const onError = () => settle(false);

        transition.addCleanup(() => settle(false));
        image.addEventListener('load', onLoad);
        image.addEventListener('error', onError);

        if (image.complete) {
          if (hasDrawablePixels()) decodeAfterLoad(false);
          else onError();
        }
      });
    }

    trackSwiper(element, swiper = element?.swiper) {
      if (!swiper) return null;

      this.swipers.add(swiper);
      if (element) this.swiperElements.set(swiper, element);
      return swiper;
    }

    swiperFor(element) {
      const swiper = element?.swiper;
      if (swiper?.destroyed) {
        try {
          delete element.swiper;
        } catch {
          element.swiper = null;
        }
        return null;
      }

      return this.trackSwiper(element, swiper);
    }

    createSwiper(element, options) {
      if (typeof window.Swiper !== 'function' || !element) return null;

      const existing = this.swiperFor(element);
      if (existing) return existing;

      const swiper = new window.Swiper(element, options);
      return this.trackSwiper(element, swiper);
    }

    destroySwiper(element, swiper = element?.swiper) {
      if (!swiper && element) {
        swiper = Array.from(this.swipers).find(
          (candidate) => this.swiperElements.get(candidate) === element
        );
      }
      if (!swiper) return;

      try {
        if (typeof swiper.destroy === 'function') swiper.destroy(true, true);
      } catch {
      }

      this.swipers.delete(swiper);
      this.swiperElements.delete(swiper);
      if (element?.swiper === swiper) {
        try {
          delete element.swiper;
        } catch {
          element.swiper = null;
        }
      }
      if (element) delete element.dataset.ultraMobileSwiperBound;
    }

    versionGalleryElements(scope) {
      if (!scope) return [];

      const elements = [];
      [
        '[data-ebike-version-gallery-main]',
        '[data-ebike-version-gallery-thumbs]',
        '[data-ebike-version-gallery-thumbs-container]'
      ].forEach((selector) => {
        if (scope.matches?.(selector)) elements.push(scope);
        scope.querySelectorAll?.(selector).forEach((element) => elements.push(element));
      });
      return Array.from(new Set(elements));
    }

    destroyVersionGalleryGroup(group) {
      this.versionGalleryElements(group).forEach((element) => {
        this.destroySwiper(element);
      });
    }

    destroyInactiveVersionGallerySwipers(groups, selectedGroups) {
      const selected = new Set(selectedGroups);
      groups.forEach((group) => {
        if (!selected.has(group)) this.destroyVersionGalleryGroup(group);
      });
    }

    destroyAllVersionGallerySwipers() {
      this.versionGalleryElements(this.root).forEach((element) => {
        this.destroySwiper(element);
      });

      this.swipers.forEach((swiper) => {
        const element = this.swiperElements.get(swiper);
        this.destroySwiper(element, swiper);
      });
      this.swipers.clear();
      this.swiperElements.clear();
    }

    matchingGroups(panel, color, family, size) {
      const groups = Array.from(panel.querySelectorAll('[data-ebike-version-gallery-group]'));
      const matchesColorFamily = (group) => (
        normalize(group.dataset.ebikeGalleryColor) === normalize(color)
        && normalize(group.dataset.ebikeGalleryFamily) === family
      );
      const colorFamilyGroups = groups.filter(matchesColorFamily);
      const hasSizedGroups = colorFamilyGroups.some((group) => normalize(group.dataset.ebikeGallerySize));
      const sizeGroups = normalize(size) && hasSizedGroups
        ? colorFamilyGroups.filter((group) => normalize(group.dataset.ebikeGallerySize) === normalize(size))
        : colorFamilyGroups;
      const selected = sizeGroups[0];
      if (!selected) return { groups, selectedGroups: [], useVersionGroup: false };

      const key = selected.dataset.ebikeGalleryKey;
      return {
        groups,
        selectedGroups: groups.filter((group) => group.dataset.ebikeGalleryKey === key),
        useVersionGroup: true
      };
    }

    syncSurface(surface, color, family, size, generation) {
      if (!this.isCurrentGeneration(generation)) return;

      const panel = this.root.querySelector('.product-img-panel.' + surface + '-view');
      if (!panel) return;

      // A size change can temporarily clear Version. Keep the current
      // gallery visible until the shopper confirms a new Version instead of
      // falling back to the first (and potentially invalid) family.
      if (!family) return;

      const colorClass = 'alt-' + colorSlug(color);
      const { groups, selectedGroups, useVersionGroup } = this.matchingGroups(panel, color, family, size);
      const legacyGalleries = Array.from(panel.querySelectorAll('[data-ebike-legacy-gallery]'));
      const hasUsableLegacyGallery = legacyGalleries.some((gallery) => (
        normalize(gallery.dataset.ebikeGalleryColor) === normalize(color)
      ));

      // Missing gallery data should not blank a valid gallery that is already
      // on screen. Leave that state intact until a matching group or legacy
      // gallery becomes available.
      if (!useVersionGroup && !hasUsableLegacyGallery) {
        const hasVisibleVersionGroup = groups.some((group) => !group.hidden);
        const hasVisibleLegacyGallery = legacyGalleries.some((gallery) => !gallery.hidden);
        if (hasVisibleVersionGroup || hasVisibleLegacyGallery) return;
      }

      const visibleElements = this.visibleSurfaceElements(groups, legacyGalleries);
      const targetAlreadyVisible = useVersionGroup
        ? selectedGroups.length > 0 && selectedGroups.every((group) => !group.hidden)
        : legacyGalleries.some((gallery) => (
          !gallery.hidden && normalize(gallery.dataset?.ebikeGalleryColor) === normalize(color)
        ));
      const targetImage = this.firstTargetImage(
        selectedGroups,
        legacyGalleries,
        color,
        useVersionGroup,
      );

      const commit = () => {
        if (!this.isCurrentGeneration(generation)) return;
        this.destroyInactiveVersionGallerySwipers(groups, selectedGroups);

        groups.forEach((group) => {
          const isSelected = selectedGroups.includes(group);
          group.hidden = !isSelected;
          const showcases = group.matches('.product-showcase')
            ? [group, ...group.querySelectorAll('.product-showcase')]
            : Array.from(group.querySelectorAll('.product-showcase'));

          showcases.forEach((showcase) => {
            removeGalleryColorClasses(showcase);
            showcase.classList.toggle('active', isSelected);
            if (isSelected) showcase.classList.add(colorClass);
          });
        });

        legacyGalleries.forEach((gallery) => {
          const matchesColor = normalize(gallery.dataset.ebikeGalleryColor) === normalize(color);
          const isSelected = !useVersionGroup && matchesColor;
          gallery.hidden = !isSelected;
          if (gallery.classList.contains('product-showcase')) {
            removeGalleryColorClasses(gallery);
            gallery.classList.toggle('active', isSelected);
            if (isSelected) gallery.classList.add(colorClass);
          }
        });

        if (useVersionGroup) {
          this.scheduleSurfaceInitialization(
            surface,
            () => this.initializeAndReset(surface, selectedGroups, generation),
            generation
          );
        } else {
          this.scheduleSurfaceInitialization(
            surface,
            () => this.resetLegacy(panel, color, generation),
            generation
          );
        }
      };

      // There is no image to gate in lightweight/legacy markup. Preserve the
      // historical synchronous behavior for that case and for an already
      // visible target. Real Ultra version groups always provide a main image.
      if (!targetImage || targetAlreadyVisible) {
        commit();
        return;
      }

      const transition = this.beginSurfaceTransition(surface);
      this.restoreVisibleSurface(visibleElements, color);
      this.waitForImage(targetImage, generation, transition).then((ready) => {
        if (!transition.active) return;
        transition.finish();
        if (ready) commit();
      });
    }

    initializeAndReset(surface, groups, generation) {
      if (!this.isCurrentGeneration(generation)) return;

      const main = groups
        .map((group) => selfOrDescendant(group, '[data-ebike-version-gallery-main]'))
        .find(Boolean);
      const thumbs = groups
        .map((group) => selfOrDescendant(group, '[data-ebike-version-gallery-thumbs]'))
        .find(Boolean);
      if (!main) return;

      if (typeof window.Swiper === 'function') {
        if (surface === 'desktop') {
          const thumbsContainer = thumbs?.querySelector('[data-ebike-version-gallery-thumbs-container]');
          let thumbsSwiper = this.swiperFor(thumbsContainer);
          if (thumbsContainer && !thumbsSwiper) {
            thumbsSwiper = this.createSwiper(thumbsContainer, {
              direction: 'vertical',
              loop: false,
              slidesPerView: Math.max(1, Math.floor((thumbsContainer.clientHeight || 280) / 70)),
              slidesPerGroup: Math.max(1, Math.floor((thumbsContainer.clientHeight || 280) / 70)),
              spaceBetween: 10,
              navigation: {
                nextEl: thumbs.querySelector('.sync2_slider__next'),
                prevEl: thumbs.querySelector('.sync2_slider__prev')
              }
            });
          }
          const mainGroup = main.closest('[data-ebike-version-gallery-group]');
          this.createSwiper(main, {
            direction: 'horizontal',
            loop: false,
            centeredSlides: true,
            allowTouchMove: true,
            navigation: {
              nextEl: mainGroup?.querySelector('.slider__next'),
              prevEl: mainGroup?.querySelector('.slider__prev')
            },
            thumbs: { swiper: thumbsSwiper }
          });
        } else {
          let thumbsSwiper = this.swiperFor(thumbs);
          if (thumbs && !thumbsSwiper) {
            thumbsSwiper = this.createSwiper(thumbs, {
              loop: false,
              speed: 400,
              spaceBetween: 15,
              freeMode: true,
              slidesPerView: 5.5,
              slidesOffsetAfter: 17
            });
          }
          this.createSwiper(main, {
            direction: 'horizontal',
            loop: false,
            speed: 400,
            spaceBetween: 24,
            centeredSlides: true,
            allowTouchMove: true,
            scrollbar: {
              el: main.querySelector('.swiper-scrollbar'),
              draggable: true,
              hide: false,
              snapOnRelease: true
            },
            thumbs: { swiper: thumbsSwiper }
          });
        }
      }

      const thumbsSwiper = surface === 'desktop'
        ? this.swiperFor(thumbs?.querySelector('[data-ebike-version-gallery-thumbs-container]'))
        : this.swiperFor(thumbs);
      const mainSwiper = this.swiperFor(main);
      mainSwiper?.update();
      thumbsSwiper?.update();
      mainSwiper?.slideTo(0, 0);
      thumbsSwiper?.slideTo(0, 0);
      this.updateMobileButtons(main, surface);
    }

    resetLegacy(panel, color, generation) {
      if (!this.isCurrentGeneration(generation)) return;

      const main = Array.from(panel.querySelectorAll('[data-ebike-legacy-main]')).find(
        (gallery) => !gallery.hidden && normalize(gallery.dataset.ebikeGalleryColor) === normalize(color)
      );
      const thumbs = Array.from(panel.querySelectorAll('[data-ebike-legacy-thumbs]')).find(
        (gallery) => !gallery.hidden && normalize(gallery.dataset.ebikeGalleryColor) === normalize(color)
      );
      main?.swiper?.update();
      thumbs?.swiper?.update();
      main?.swiper?.slideTo(0, 0);
      thumbs?.swiper?.slideTo(0, 0);
      this.updateMobileButtons(main, panel.classList.contains('mobile-view') ? 'mobile' : 'desktop');
    }

    updateMobileButtons(main, surface) {
      if (!main || surface !== 'mobile') return;
      const hasMultipleSlides = main.querySelectorAll(':scope > .swiper-wrapper > .swiper-slide').length > 1;
      main.querySelectorAll('[data-ultra-mobile-prev], [data-ultra-mobile-next]').forEach((button) => {
        button.disabled = !hasMultipleSlides;
      });
    }

    sync(generation = this.generation) {
      if (!this.isCurrentGeneration(generation)) return;

      const color = this.selectedColor();
      if (!color) return;
      const family = this.selectedFamily();
      const size = this.selectedSize();
      this.syncSurface('desktop', color, family, size, generation);
      this.syncSurface('mobile', color, family, size, generation);
    }

    destroy() {
      this.destroyed = true;
      this.generation += 1;
      this.cancelPendingSurfaceTransitions();
      this.cancelPendingFrames();
      this.form?.removeEventListener('change', this.handleChange);
      window.removeEventListener('load', this.handleLoad);
      this.destroyAllVersionGallerySwipers();
      delete this.root.dataset.ebikeVersionGalleryReady;
    }
  }

  function initialize(scope = document) {
    const roots = [];

    const addRoot = (candidate) => {
      const root = candidate?.matches?.(ROOT_SELECTOR)
        ? candidate
        : candidate?.closest?.(ROOT_SELECTOR);
      if (!root) return;
      if (!roots.includes(root)) roots.push(root);
    };

    if (scope instanceof Element) addRoot(scope);
    scope.querySelectorAll?.(ROOT_SELECTOR).forEach(addRoot);
    roots.forEach((root) => {
      if (root.dataset.ebikeVersionGalleryReady === 'true') return;
      const instance = new VariantGallery(root);
      if (root.dataset.ebikeVersionGalleryReady === 'true') instances.set(root, instance);
    });
  }

  function destroyWithin(scope) {
    instances.forEach((instance, root) => {
      if (root === scope || scope.contains?.(root) || root.contains?.(scope)) {
        instance.destroy();
        instances.delete(root);
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialize(), { once: true });
  } else {
    initialize();
  }

  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
  document.addEventListener('shopify:section:unload', (event) => destroyWithin(event.target));
})();
